import type { GridColDef } from '@mui/x-data-grid-premium'
import type { SxProps, Theme } from '@mui/material'
import { type CSSObject } from '@mui/material/styles'
import type { Density } from './helpers/createDensityStore/createDensityStore'

export const ROW_HEIGHT: Record<Density, number> = { default: 40, relaxed: 48 }
export const HEADER_HEIGHT: Record<Density, number> = { default: 40, relaxed: 48 }

// Shared by valueFormatter (emits it) and cellClassName (matches it to tint empty cells) — keep in sync.
export const EMPTY_CELL_VALUE = '—'

// Row-height floor for wrap mode: auto-height rows would otherwise shrink to
// content. Set inline on the grid root from the active density.
export const WRAP_ROW_MIN_HEIGHT_VAR = '--SuiDataGrid-wrapRowMinHeight'

export const WRAP_MAX_LINES = 3

// Multi-line ellipsis. `display: -webkit-box` is required for `-webkit-line-clamp`;
// `WebkitBoxPack: start` top-aligns the lines; `overflowWrap: anywhere` breaks long
// unbreakable strings (SKUs, URLs) instead of overflowing to ellipsis.
// The lines past the clamp are hidden by the overflow clip alone, so it has to land on the
// content box — a padding-box clip lets the next line show through the bottom padding.
export const WRAP_CLAMP: CSSObject = {
  whiteSpace: 'normal',
  display: '-webkit-box',
  WebkitLineClamp: WRAP_MAX_LINES,
  WebkitBoxOrient: 'vertical',
  WebkitBoxPack: 'start',
  overflow: 'clip',
  overflowClipMargin: 'content-box',
  textOverflow: 'ellipsis',
  overflowWrap: 'anywhere',
}

export const suiGridClasses = {
  borderRight: 'border-right',
  borderLeft: 'border-left',
  numericTabularNums: 'numeric-tabular-nums',
  bold: 'bold',
  secondary: 'secondary',
  tertiary: 'tertiary',
  disabled: 'disabled',
  position: 'relative',
  rightAdornment: 'right-adornment',
  leftAdornment: 'left-adornment',
  activeRow: 'active-row',
  hasSelectedRowsClass: 'hasSelectedRows',
  disableGutters: 'disable-gutters',
  firstVisibleColumn: 'first-visible-column',
  lastVisibleColumn: 'last-visible-column',
  cellBackgroundSecondaryContainer: 'cell-background-secondary-container',
  customRenderCell: 'custom-render-cell',
  underlineDotted: 'underline-dotted',
  rowError: 'row-error',
  cellError: 'cell-error',
  lineItemsWrapped: 'line-items-wrapped',
  wrapTextCell: 'wrap-text-cell',
}

// Wrap mode: pin a row-height band to the top of a leading control cell so its
// widget (thumbnail, checkbox, fallback icon) sits level with the first text line
// instead of centering across all wrapped lines. Shared so every leading column
// anchors its content identically.
export const WRAP_LEADING_CELL_BAND_SX: SxProps<Theme> = {
  [`.${suiGridClasses.lineItemsWrapped} &`]: {
    height: `var(${WRAP_ROW_MIN_HEIGHT_VAR}) !important`,
    alignSelf: 'flex-start',
  },
}

export const moneyColumnDef: Partial<GridColDef> = {
  minWidth: 136,
  align: 'right' as const,
  cellClassName: suiGridClasses.numericTabularNums,
}

export const quantityColumnDef: Partial<GridColDef> = {
  minWidth: 82,
  align: 'right' as const,
  cellClassName: suiGridClasses.numericTabularNums,
}

export const DEFAULT_COLUMN_WIDTH = 128
